import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, User, Phone, Clipboard, Clock, Trash2 } from 'lucide-react';
import './ReservationDetailModal.css';

function ReservationDetailModal({ reservation, isOpen, onClose, onStatusChange, onDelete }) {
  const navigate = useNavigate();
  const [isUpdating, setIsUpdating] = useState(false);

  if (!isOpen || !reservation) return null;

  const formatDateTime = (dateTime) => {
    if (!dateTime) return '';
    const date = new Date(dateTime);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const days = ['일', '월', '화', '수', '목', '금', '토'];
    const dayOfWeek = days[date.getDay()];
    
    return `${year}년 ${month}월 ${day}일 (${dayOfWeek}) ${hours}:${minutes}`;
  };

  const getStatusText = (status) => {
    const statusMap = {
      'PENDING': '예약 대기',
      'CONFIRMED': '예약 확정',
      'COMPLETED': '시술 완료',
      'CANCELED': '예약 취소',
      'NO_SHOW': '노쇼'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'PENDING': '#FF9800',
      'CONFIRMED': '#4CAF50',
      'COMPLETED': '#9E9E9E',
      'CANCELED': '#F44336',
      'NO_SHOW': '#D32F2F'
    };
    return colorMap[status] || '#8B7355';
  };

  const handleStatusUpdate = async (newStatus) => {
    // 시술 완료 시 케어 리포트 작성 여부 확인
    if (newStatus === 'COMPLETED') {
      const goToCareReport = window.confirm('케어 리포트를 작성하러 가시겠습니까?');

      setIsUpdating(true);
      try {
        await onStatusChange(reservation.id, newStatus);
        onClose();

        // 케어 리포트 작성 확인 시 페이지 이동
        if (goToCareReport) {
          navigate('/admin/care-reports', {
            state: {
              customerName: reservation.customerName,
              customerPhone: reservation.customerPhone,
              autoSearch: true
            }
          });
        }
      } catch (error) {
        alert('상태 변경에 실패했습니다.');
        console.error(error);
      } finally {
        setIsUpdating(false);
      }
      return;
    }

    // 다른 상태 변경은 기존 로직대로
    if (!window.confirm(`예약 상태를 "${getStatusText(newStatus)}"(으)로 변경하시겠습니까?`)) {
      return;
    }

    setIsUpdating(true);
    try {
      await onStatusChange(reservation.id, newStatus);
      onClose();
    } catch (error) {
      alert('상태 변경에 실패했습니다.');
      console.error(error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('정말로 이 예약을 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.')) {
      return;
    }

    setIsUpdating(true);
    try {
      await onDelete(reservation.id);
      onClose();
    } catch (error) {
      alert('예약 삭제에 실패했습니다.');
      console.error(error);
    } finally {
      setIsUpdating(false);
    }
  };

  // 현재 상태에 따른 액션 버튼 렌더링
  const renderActionButtons = () => {
    if (isUpdating) {
      return (
        <div className="modal-actions">
          <button className="action-button updating" disabled>
            처리 중...
          </button>
        </div>
      );
    }

    switch (reservation.status) {
      case 'PENDING':
        return (
          <div className="modal-actions">
            <button
              className="action-button confirm"
              onClick={() => handleStatusUpdate('CONFIRMED')}
            >
              예약 확정
            </button>
            <button
              className="action-button cancel"
              onClick={() => handleStatusUpdate('CANCELED')}
            >
              예약 취소
            </button>
          </div>
        );
      
      case 'CONFIRMED':
        return (
          <div className="modal-actions">
            <button 
              className="action-button complete"
              onClick={() => handleStatusUpdate('COMPLETED')}
            >
              시술 완료
            </button>
            <button 
              className="action-button noshow"
              onClick={() => handleStatusUpdate('NO_SHOW')}
            >
              노쇼 처리
            </button>
            <button 
              className="action-button cancel"
              onClick={() => handleStatusUpdate('CANCELED')}
            >
              예약 취소
            </button>
          </div>
        );
      
      case 'COMPLETED':
      case 'CANCELED':
      case 'NO_SHOW':
        return (
          <div className="modal-actions">
            <p className="status-message">
              이 예약은 이미 처리가 완료되었습니다.
            </p>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2 className="modal-title">예약 상세</h2>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {/* 상태 배지 */}
          <div 
            className="status-badge"
            style={{ backgroundColor: getStatusColor(reservation.status) }}
          >
            {getStatusText(reservation.status)}
          </div>

          {/* 예약 정보 */}
          <div className="info-section">
            <div className="info-item">
              <div className="info-icon">
                <User size={20} />
              </div>
              <div className="info-content">
                <p className="info-label">고객명</p>
                <p className="info-value">{reservation.customerName}</p>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <Phone size={20} />
              </div>
              <div className="info-content">
                <p className="info-label">연락처</p>
                <p className="info-value">{reservation.customerPhone}</p>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <Clipboard size={20} />
              </div>
              <div className="info-content">
                <p className="info-label">시술 메뉴</p>
                <p className="info-value">{reservation.treatment}</p>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <Clock size={20} />
              </div>
              <div className="info-content">
                <p className="info-label">예약 시간</p>
                <p className="info-value">{formatDateTime(reservation.reservationTime)}</p>
              </div>
            </div>
          </div>

          {/* 액션 버튼 */}
          {renderActionButtons()}

          {/* 삭제 버튼 (항상 표시) */}
          <div className="modal-footer">
            <button 
              className="delete-button"
              onClick={handleDelete}
              disabled={isUpdating}
            >
              <Trash2 size={18} />
              예약 삭제
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReservationDetailModal;
